import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, BadgeCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Stars = ({ value }) => (
  <span className="inline-flex">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} className={i < value ? "text-amber-500" : "text-slate-300"} fill="currentColor" />
    ))}
  </span>
);

export default function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews", { params: query ? { q: query } : {} });
      setReviews(r.data || []);
    } catch {
      toast.error("Could not load reviews");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (review) => {
    if (!window.confirm(`Delete this ${review.rating}-star review by ${review.name}? This cannot be undone.`)) return;
    setDeleting(review.id);
    try {
      await api.delete(`/admin/reviews/${review.id}`);
      toast.success("Review deleted");
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="mt-8 space-y-6 max-w-6xl" data-testid="reviews-panel">
      <header className="border-b border-[#E5E1D8] pb-4">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Community</p>
        <h2 className="font-serif text-3xl mt-1">Customer reviews</h2>
        <p className="text-sm text-[#1A1714]/70 mt-2 max-w-2xl">
          Every review submitted on the storefront appears here. Delete inappropriate content —
          the book's average rating updates automatically.
        </p>
      </header>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search reviewer, text, or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
          className="rounded-none h-10 max-w-md"
          data-testid="reviews-filter"
        />
        <Button
          onClick={() => load(q)}
          variant="outline"
          className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
          data-testid="reviews-search-btn"
        >
          Search
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#1A1714]/60 italic">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-[#1A1714]/60 italic py-8 text-center border border-[#E5E1D8]" data-testid="reviews-empty">
          No reviews yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E1D8]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 border-b border-[#E5E1D8]">
                <th className="py-2 px-3">Book</th>
                <th className="py-2 px-3">Rating</th>
                <th className="py-2 px-3">Review</th>
                <th className="py-2 px-3">Reviewer</th>
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className="border-b border-[#E5E1D8]/60 align-top" data-testid={`admin-review-row-${r.id}`}>
                  <td className="py-2 px-3 max-w-[200px]">
                    <span className="font-medium block truncate">{r.book_title || "—"}</span>
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap"><Stars value={r.rating} /></td>
                  <td className="py-2 px-3 max-w-[360px]">
                    {r.title && <span className="font-medium block">{r.title}</span>}
                    <span className="text-[#1A1714]/70 line-clamp-2">{r.body}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="block">{r.name}</span>
                    <span className="block text-xs text-[#1A1714]/50">{r.email}</span>
                    {r.verified_purchase && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium mt-0.5" data-testid={`admin-review-verified-${r.id}`}>
                        <BadgeCheck size={12} /> Verified
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-[#1A1714]/60 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      onClick={() => remove(r)}
                      disabled={deleting === r.id}
                      variant="outline"
                      className="rounded-none border-red-300 text-red-700 hover:bg-red-50 font-mono text-xs uppercase tracking-editorial"
                      data-testid={`admin-review-delete-${r.id}`}
                    >
                      <Trash2 size={12} className="mr-1" /> {deleting === r.id ? "…" : "Delete"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
